import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminDeposits = () => {
  const [rawDeposits, setRawDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchDeposits();
  }, [debouncedSearch]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getDeposits({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawDeposits(response.data.deposits);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  const filteredDeposits = statusFilter
    ? rawDeposits.filter((d) => d.status === statusFilter)
    : rawDeposits;
  const total = filteredDeposits.length;
  const deposits = filteredDeposits.slice((page - 1) * pageSize, page * pageSize);

  const handleDepositAction = async (depositId, status) => {
    try {
      await adminAPI.depositAction({ deposit_id: depositId, status });
      toast.success('Deposit updated');
      fetchDeposits();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  if (loading && rawDeposits.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-deposits">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Deposit Management</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Amount</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Date</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map((deposit) => (
                <tr key={deposit.id} className="table-row" data-testid={`deposit-row-${deposit.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{deposit.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{deposit.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground">{deposit.asset_symbol}</td>
                  <td className="py-4 text-foreground">{deposit.amount}</td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(deposit.status)}`}>
                      {formatStatusText(deposit.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(deposit.created_at)}</td>
                  <td className="py-4">
                    {deposit.status === 'pending' && (
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={() => handleDepositAction(deposit.id, 'confirmed')} className="btn-primary" data-testid={`approve-deposit-btn-${deposit.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" onClick={() => handleDepositAction(deposit.id, 'rejected')} className="bg-red-600 hover:bg-red-700" data-testid={`reject-deposit-btn-${deposit.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>
    </div>
  );
};

export default AdminDeposits;
